import React, { useState, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Phone } from "@/components/Phone";
import { RemovableListItem } from "./RemovableListItem";
import { useTheme } from "@/hooks/useTheme";
import { formatPhoneNumber, validatePhoneNumber } from "@/utils/phoneValidation";

export type PhoneType = "cell" | "home" | "work";

export type PhoneData = {
  type: PhoneType;
  number: string;
};

export interface PhoneListEditorProps {
  phones: PhoneData[];
  onChange: (phones: PhoneData[]) => void;
  editing?: boolean;
  onPhonePress?: (phone: PhoneData) => void;
  /** Error message for cell phone uniqueness check */
  cellPhoneError?: string;
  /** Whether cell phone uniqueness check is in progress */
  cellPhoneChecking?: boolean;
}

const PHONE_TYPE_COLORS = {
  cell: "#10b981", // green
  home: "#3b82f6", // blue
  work: "#8b5cf6", // purple
};

export function PhoneListEditor({
  phones,
  onChange,
  editing = false,
  onPhonePress,
  cellPhoneError,
  cellPhoneChecking,
}: PhoneListEditorProps) {
  const { colors } = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});

  // Calculate which phone types are available (not already used)
  const usedTypes = useMemo(() => new Set(phones.map((p) => p.type)), [phones]);

  const getAvailableTypes = (currentType: PhoneType): { text: PhoneType; bgColor: string }[] => {
    const allTypes: PhoneType[] = ["cell", "home", "work"];
    return allTypes
      .filter((type) => type === currentType || !usedTypes.has(type))
      .map((type) => ({
        text: type,
        bgColor: PHONE_TYPE_COLORS[type],
      }));
  };

  const handlePhoneUpdate = (index: number, updatedPhone: PhoneData) => {
    // Validate if changing the number
    if (updatedPhone.number !== phones[index].number) {
      const formatted = formatPhoneNumber(updatedPhone.number);
      const isValid = validatePhoneNumber(formatted);

      if (!isValid && formatted.length > 0) {
        setValidationErrors({ ...validationErrors, [index]: "Invalid phone number" });
      } else {
        const newErrors = { ...validationErrors };
        delete newErrors[index];
        setValidationErrors(newErrors);
      }

      updatedPhone.number = formatted;
    }

    const newPhones = [...phones];
    newPhones[index] = updatedPhone;
    onChange(newPhones);
  };

  const handleRemovePhone = (index: number) => {
    const newPhones = phones.filter((_, i) => i !== index);
    const newErrors = { ...validationErrors };
    delete newErrors[index];
    setValidationErrors(newErrors);
    onChange(newPhones);
  };

  // Non-editing state: Use Phone.tsx component
  if (!editing) {
    if (!phones || phones.length === 0) {
      return <ThemedText style={{ opacity: 0.5 }}>No phones</ThemedText>;
    }

    return (
      <View style={{ gap: 8 }}>
        {phones.map((phone, index) => (
          <Phone key={index} type={phone.type === "work" ? "office" : phone.type} number={phone.number} />
        ))}
      </View>
    );
  }

  // Editing state: Use RemovableListItem
  return (
    <RemovableListItem
      items={phones}
      editing={editing}
      hoveredIndex={hoveredIndex}
      onHoverIn={setHoveredIndex}
      onHoverOut={() => setHoveredIndex(null)}
      onRemove={handleRemovePhone}
      emptyMessage="No phones added"
      renderContent={(phone, index) => (
        <View style={styles.phoneEditContent}>
          <View style={styles.phoneInputRow}>
            <View style={[styles.typeColorIndicator, { backgroundColor: PHONE_TYPE_COLORS[phone.type] }]} />
            <ThemedInput
              testID={`phone-type-${index}`}
              placeholder="Type"
              value={phone.type}
              onChangeText={(type) => handlePhoneUpdate(index, { ...phone, type: type as PhoneType })}
              items={getAvailableTypes(phone.type)}
              style={styles.typeInput}
            />
            <ThemedInput
              testID={`phone-number-${index}`}
              placeholder="(555) 555-5555"
              value={phone.number}
              onChangeText={(number) => handlePhoneUpdate(index, { ...phone, number })}
              keyboardType="phone-pad"
              style={styles.numberInput}
            />
          </View>
          {validationErrors[index] && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>{validationErrors[index]}</ThemedText>
          )}
          {/* Show cell phone uniqueness error */}
          {phone.type === "cell" && cellPhoneError && !validationErrors[index] && (
            <ThemedText style={[styles.errorText, { color: colors.dangerText }]}>
              {cellPhoneChecking ? "Checking..." : cellPhoneError}
            </ThemedText>
          )}
        </View>
      )}
    />
  );
}

const styles = StyleSheet.create({
  phoneEditContent: {
    flex: 1,
    gap: 4,
  },
  phoneInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  typeColorIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  typeInput: {
    flex: 1,
    minWidth: 100,
  },
  numberInput: {
    flex: 2,
    minWidth: 150,
  },
  errorText: {
    fontSize: 12,
    marginTop: 2,
  },
});
